import { create } from 'zustand';
import apiService from '../services/apiService';

const DEFAULT_STAGE_PROBABILITIES = {
  prospecting: 10,
  qualification: 25,
  needs_analysis: 40,
  proposal: 60,
  negotiation: 80,
  closed_won: 100,
  closed_lost: 0,
};

const useDealStore = create((set, get) => ({
  // State
  deals: [],
  currentDeal: null,
  isLoading: false,
  error: null,
  stageProbabilities: { ...DEFAULT_STAGE_PROBABILITIES },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  },
  filters: {
    stage: null,
    assignedTo: null,
    source: null,
    priority: null,
    search: null
  },
  stats: null,
  pipeline: [],
  upcomingDeals: [],

  // Actions
  fetchDeals: async (params = {}) => {
    set({ isLoading: true, error: null });
    try {
      const queryParams = { ...get().filters, ...params };
      const response = await apiService.get('/deals', { params: queryParams });
      const { deals, pagination } = response.data.data;
      
      set({
        deals,
        pagination,
        isLoading: false,
        error: null
      });
      
      return { success: true, deals, pagination };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch deals';
      set({
        deals: [],
        isLoading: false,
        error: errorMessage
      });
      return { success: false, error: errorMessage };
    }
  },

  fetchDealById: async (dealId) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.get(`/deals/${dealId}`);
      const deal = response.data.data;
      
      set({
        currentDeal: deal,
        isLoading: false,
        error: null
      });
      
      return { success: true, deal };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch deal';
      set({
        currentDeal: null,
        isLoading: false,
        error: errorMessage
      });
      return { success: false, error: errorMessage };
    }
  },

  createDeal: async (dealData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.post('/deals', dealData);
      const newDeal = response.data.data;
      
      set((state) => ({
        deals: [newDeal, ...state.deals],
        isLoading: false,
        error: null
      }));
      
      return { success: true, deal: newDeal };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create deal';
      set({
        isLoading: false,
        error: errorMessage
      });
      return { success: false, error: errorMessage };
    }
  },

  updateDeal: async (dealId, dealData) => {
    set({ isLoading: true, error: null });
    try {
      console.log('Updating deal with data:', dealData);
      const response = await apiService.put(`/deals/${dealId}`, dealData);
      const updatedDeal = response.data.data;
      
      set((state) => ({
        deals: state.deals.map(deal => 
          deal._id === dealId ? updatedDeal : deal
        ),
        currentDeal: state.currentDeal?._id === dealId ? updatedDeal : state.currentDeal,
        isLoading: false,
        error: null
      }));
      
      return { success: true, deal: updatedDeal };
    } catch (error) {
      console.error('Deal update error:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || 'Failed to update deal';
      set({
        isLoading: false,
        error: errorMessage
      });
      return { success: false, error: errorMessage };
    }
  },

  deleteDeal: async (dealId) => {
    set({ isLoading: true, error: null });
    try {
      await apiService.delete(`/deals/${dealId}`);
      
      set((state) => ({
        deals: state.deals.filter(deal => deal._id !== dealId),
        currentDeal: state.currentDeal?._id === dealId ? null : state.currentDeal,
        isLoading: false,
        error: null
      }));
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete deal';
      set({
        isLoading: false,
        error: errorMessage
      });
      return { success: false, error: errorMessage };
    }
  },

  fetchStageProbabilities: async () => {
    try {
      const response = await apiService.get('/deals/stage-probabilities');
      const probabilities = response.data.data || {};

      set({
        stageProbabilities: {
          ...DEFAULT_STAGE_PROBABILITIES,
          ...probabilities,
        },
      });

      return { success: true, probabilities };
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to fetch stage probabilities';
      return { success: false, error: errorMessage };
    }
  },

  updateStage: async (dealId, stage, lostReason = null) => {
    set({ isLoading: true, error: null });
    try {
      const response = await apiService.put(`/deals/${dealId}/stage`, { 
        stage, 
        lostReason 
      });
      const updatedDeal = response.data.data;
      
      set((state) => ({
        deals: state.deals.map(deal => 
          deal._id === dealId ? updatedDeal : deal
        ),
        currentDeal: state.currentDeal?._id === dealId ? updatedDeal : state.currentDeal,
        isLoading: false,
        error: null
      }));
      
      return { success: true, deal: updatedDeal };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update stage';
      set({
        isLoading: false,
        error: errorMessage
      });
      return { success: false, error: errorMessage };
    }
  },

  addNote: async (dealId, content) => {
    try {
      const response = await apiService.post(`/deals/${dealId}/notes`, { content });
      const note = response.data.data;
      
      set((state) => ({
        currentDeal: state.currentDeal?._id === dealId ? {
          ...state.currentDeal,
          notes: [...(state.currentDeal.notes || []), note]
        } : state.currentDeal
      }));
      
      return { success: true, note };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to add note';
      return { success: false, error: errorMessage };
    }
  },

  updateNote: async (dealId, noteId, content) => {
    try {
      const response = await apiService.put(`/deals/${dealId}/notes/${noteId}`, { content });
      const updatedNote = response.data.data;
      
      set((state) => ({
        currentDeal: state.currentDeal?._id === dealId ? {
          ...state.currentDeal,
          notes: state.currentDeal.notes?.map(note => 
            note._id === noteId ? updatedNote : note
          ) || []
        } : state.currentDeal
      }));
      
      return { success: true, note: updatedNote };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update note';
      return { success: false, error: errorMessage };
    }
  },

  deleteNote: async (dealId, noteId) => {
    try {
      await apiService.delete(`/deals/${dealId}/notes/${noteId}`);
      
      set((state) => ({
        currentDeal: state.currentDeal?._id === dealId ? {
          ...state.currentDeal,
          notes: state.currentDeal.notes?.filter(note => note._id !== noteId) || []
        } : state.currentDeal
      }));
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete note';
      return { success: false, error: errorMessage };
    }
  },

  addActivity: async (dealId, activityData) => {
    try {
      const response = await apiService.post(`/deals/${dealId}/activities`, activityData);
      const activity = response.data.data;
      
      set((state) => ({
        currentDeal: state.currentDeal?._id === dealId ? {
          ...state.currentDeal,
          activities: [...(state.currentDeal.activities || []), activity]
        } : state.currentDeal
      }));
      
      return { success: true, activity };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to add activity';
      return { success: false, error: errorMessage };
    }
  },

  // Requirements
  addRequirement: async (dealId, requirementData) => {
    try {
      const response = await apiService.post(`/deals/${dealId}/requirements`, requirementData);
      const requirement = response.data.data;
      
      set((state) => ({
        currentDeal: state.currentDeal?._id === dealId ? {
          ...state.currentDeal,
          requirements: [...(state.currentDeal.requirements || []), requirement]
        } : state.currentDeal
      }));
      
      return { success: true, requirement };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to add requirement';
      return { success: false, error: errorMessage };
    }
  },

  updateRequirement: async (dealId, requirementId, requirementData) => {
    try {
      const response = await apiService.put(`/deals/${dealId}/requirements/${requirementId}`, requirementData);
      const updatedRequirement = response.data.data;
      
      set((state) => ({
        currentDeal: state.currentDeal?._id === dealId ? {
          ...state.currentDeal,
          requirements: state.currentDeal.requirements?.map(req => 
            req._id === requirementId ? updatedRequirement : req
          ) || []
        } : state.currentDeal
      }));
      
      return { success: true, requirement: updatedRequirement };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update requirement';
      return { success: false, error: errorMessage };
    }
  },

  deleteRequirement: async (dealId, requirementId) => {
    try {
      await apiService.delete(`/deals/${dealId}/requirements/${requirementId}`);
      
      set((state) => ({
        currentDeal: state.currentDeal?._id === dealId ? {
          ...state.currentDeal,
          requirements: state.currentDeal.requirements?.filter(req => req._id !== requirementId) || []
        } : state.currentDeal
      }));
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete requirement';
      return { success: false, error: errorMessage };
    }
  },

  // Meetings
  addMeeting: async (dealId, meetingData) => {
    try {
      const response = await apiService.post(`/deals/${dealId}/meetings`, meetingData);
      const meeting = response.data.data;
      
      set((state) => ({
        currentDeal: state.currentDeal?._id === dealId ? {
          ...state.currentDeal,
          meetings: [...(state.currentDeal.meetings || []), meeting]
        } : state.currentDeal
      }));
      
      return { success: true, meeting };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to add meeting';
      return { success: false, error: errorMessage };
    }
  },

  updateMeeting: async (dealId, meetingId, meetingData) => {
    try {
      const response = await apiService.put(`/deals/${dealId}/meetings/${meetingId}`, meetingData);
      const updatedMeeting = response.data.data;
      
      set((state) => ({
        currentDeal: state.currentDeal?._id === dealId ? {
          ...state.currentDeal,
          meetings: state.currentDeal.meetings?.map(meeting => 
            meeting._id === meetingId ? updatedMeeting : meeting
          ) || []
        } : state.currentDeal
      }));
      
      return { success: true, meeting: updatedMeeting };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update meeting';
      return { success: false, error: errorMessage };
    }
  },

  deleteMeeting: async (dealId, meetingId) => {
    try {
      await apiService.delete(`/deals/${dealId}/meetings/${meetingId}`);
      
      set((state) => ({
        currentDeal: state.currentDeal?._id === dealId ? {
          ...state.currentDeal,
          meetings: state.currentDeal.meetings?.filter(meeting => meeting._id !== meetingId) || []
        } : state.currentDeal
      }));
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete meeting';
      return { success: false, error: errorMessage };
    }
  },

  // Calls
  addCall: async (dealId, callData) => {
    try {
      const response = await apiService.post(`/deals/${dealId}/calls`, callData);
      const call = response.data.data;
      
      set((state) => ({
        currentDeal: state.currentDeal?._id === dealId ? {
          ...state.currentDeal,
          calls: [...(state.currentDeal.calls || []), call]
        } : state.currentDeal
      }));
      
      return { success: true, call };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to add call';
      return { success: false, error: errorMessage };
    }
  },

  updateCall: async (dealId, callId, callData) => {
    try {
      const response = await apiService.put(`/deals/${dealId}/calls/${callId}`, callData);
      const updatedCall = response.data.data;
      
      set((state) => ({
        currentDeal: state.currentDeal?._id === dealId ? {
          ...state.currentDeal,
          calls: state.currentDeal.calls?.map(call => 
            call._id === callId ? updatedCall : call
          ) || []
        } : state.currentDeal
      }));
      
      return { success: true, call: updatedCall };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update call';
      return { success: false, error: errorMessage };
    }
  },

  deleteCall: async (dealId, callId) => {
    try {
      await apiService.delete(`/deals/${dealId}/calls/${callId}`);
      
      set((state) => ({
        currentDeal: state.currentDeal?._id === dealId ? {
          ...state.currentDeal,
          calls: state.currentDeal.calls?.filter(call => call._id !== callId) || []
        } : state.currentDeal
      }));
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete call';
      return { success: false, error: errorMessage };
    }
  },

  // Documents
  addDocument: async (dealId, documentData) => {
    try {
      const response = await apiService.post(`/deals/${dealId}/documents`, documentData);
      const document = response.data.data;
      
      set((state) => ({
        currentDeal: state.currentDeal?._id === dealId ? {
          ...state.currentDeal,
          documents: [...(state.currentDeal.documents || []), document]
        } : state.currentDeal
      }));
      
      return { success: true, document };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to add document';
      return { success: false, error: errorMessage };
    }
  },

  deleteDocument: async (dealId, documentId) => {
    try {
      await apiService.delete(`/deals/${dealId}/documents/${documentId}`);
      
      set((state) => ({
        currentDeal: state.currentDeal?._id === dealId ? {
          ...state.currentDeal,
          documents: state.currentDeal.documents?.filter(doc => doc._id !== documentId) || []
        } : state.currentDeal
      }));
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete document';
      return { success: false, error: errorMessage };
    }
  },

  fetchDealStats: async () => {
    try {
      const response = await apiService.get('/deals/stats');
      const stats = response.data.data;
      
      set({ stats });
      return { success: true, stats };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch stats';
      return { success: false, error: errorMessage };
    }
  },

  fetchPipeline: async () => {
    try {
      const response = await apiService.get('/deals/pipeline');
      const pipeline = response.data.data;
      
      set({ pipeline });
      return { success: true, pipeline };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch pipeline';
      return { success: false, error: errorMessage };
    }
  },

  fetchUpcomingDeals: async (days = 30) => {
    try {
      const response = await apiService.get('/deals/upcoming', { 
        params: { days } 
      });
      const upcomingDeals = response.data.data;
      
      set({ upcomingDeals });
      return { success: true, upcomingDeals };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to fetch upcoming deals';
      return { success: false, error: errorMessage };
    }
  },

  // Task management
  addTask: async (dealId, taskData) => {
    try {
      const response = await apiService.post(`/deals/${dealId}/tasks`, taskData);
      if (response.data.success) {
        // Refresh the current deal to get updated tasks
        await get().fetchDealById(dealId);
        return { success: true };
      }
      return { success: false, error: response.data.message };
    } catch (error) {
      const details = error.response?.data?.details;
      const errorMessage = error.response?.data?.message || 'Failed to add task';

      // Handle both array and object formats for validation details
      let errorDetails = null;
      if (Array.isArray(details) && details.length > 0) {
        errorDetails = details;
      } else if (details && typeof details === 'object') {
        // Convert object of errors to array of messages
        errorDetails = Object.values(details).map(err => err.message || err);
      }

      return {
        success: false,
        error: errorMessage,
        details: errorDetails
      };
    }
  },

  updateTask: async (dealId, taskId, taskData) => {
    try {
      const response = await apiService.put(`/deals/${dealId}/tasks/${taskId}`, taskData);
      if (response.data.success) {
        // Refresh the current deal to get updated tasks
        await get().fetchDealById(dealId);
        return { success: true };
      }
      return { success: false, error: response.data.message };
    } catch (error) {
      const details = error.response?.data?.details;
      const errorMessage = error.response?.data?.message || 'Failed to update task';

      // Handle both array and object formats for validation details
      let errorDetails = null;
      if (Array.isArray(details) && details.length > 0) {
        errorDetails = details;
      } else if (details && typeof details === 'object') {
        // Convert object of errors to array of messages
        errorDetails = Object.values(details).map(err => err.message || err);
      }

      return {
        success: false,
        error: errorMessage,
        details: errorDetails
      };
    }
  },

  deleteTask: async (dealId, taskId) => {
    try {
      const response = await apiService.delete(`/deals/${dealId}/tasks/${taskId}`);
      if (response.data.success) {
        // Refresh the current deal to get updated tasks
        await get().fetchDealById(dealId);
        return { success: true };
      }
      return { success: false, error: response.data.message };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to delete task';
      return { success: false, error: errorMessage };
    }
  },

  setFilters: (newFilters) => {
    set((state) => ({
      filters: { ...state.filters, ...newFilters }
    }));
  },

  clearFilters: () => {
    set({
      filters: {
        stage: null,
        assignedTo: null,
        source: null,
        priority: null,
        search: ''
      }
    });
  },

  clearError: () => set({ error: null }),

  clearCurrentDeal: () => set({ currentDeal: null })
}));

export default useDealStore;
